import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isArticlePage = location.pathname !== '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const navBg = scrolled || isArticlePage
    ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
    : 'bg-transparent';

  const textClass = scrolled || isArticlePage
    ? 'text-foreground-950'
    : 'text-white';

  const pillBorder = scrolled || isArticlePage
    ? 'border-foreground-300 hover:border-foreground-500'
    : 'border-white/60 hover:border-white hover:bg-white/10';

  const outlineHover = scrolled || isArticlePage
    ? 'hover:bg-foreground-950 hover:text-background-50'
    : 'hover:bg-white hover:text-foreground-950';

  const handleCategoryClick = () => {
    setDropdownOpen(false);
    setMobileCatOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="flex items-center justify-between h-[72px] px-4 md:px-6">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className={`flex items-center gap-2 shrink-0 cursor-pointer ${textClass}`}
          >
            <span className="font-heading text-2xl font-semibold tracking-tight">
              OOPBuy
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center ${textClass} hover:bg-foreground-950/5`}
            >
              Home
            </Link>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center ${textClass} hover:bg-foreground-950/5`}
            >
              Shopping Guide
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 h-10 px-5 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${pillBorder} ${textClass}`}
            >
              <span>All Categories</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[540px] max-w-[calc(100vw-2rem)] bg-background-50 rounded-2xl shadow-lg border border-background-200/70 py-2 z-50">
                <div className="grid grid-cols-2 gap-1 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center h-11 px-4 text-sm text-foreground-800 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:flex items-center h-10 px-6 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${pillBorder} ${textClass} ${outlineHover}`}
          >
            oopbuy spreadsheet
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center h-10 px-6 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            Buy It Now
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-full cursor-pointer ${textClass}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center h-11 px-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              <i className="ri-home-4-line mr-3 text-foreground-500"></i>
              Home
            </Link>
            <Link
              to="/oopbuy-spreadsheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center h-11 px-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              <i className="ri-book-open-line mr-3 text-foreground-500"></i>
              Shopping Guide
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between h-11 px-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer w-full"
            >
              <span className="flex items-center">
                <i className="ri-grid-line mr-3 text-foreground-500"></i>
                All Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-3 border-l-2 border-background-200 ml-4 mt-1 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={handleCategoryClick}
                    className="flex items-center h-10 px-3 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center h-11 px-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer mt-2"
            >
              <i className="ri-external-link-line mr-3 text-foreground-500"></i>
              oopbuy spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}